type Localized = { languageID: string; text: string }[];

// `useLocalize` returns undefined when the list is missing or every entry is blank.
type Localize = (value: Localized) => { text?: string | null } | undefined;

type SuccessionUser = {
	id: string;
	currentJobPosition?: {
		jobTitle?: { title?: Localized | null } | null;
	} | null;
};

export type SuccessionNomination<TUser extends SuccessionUser = SuccessionUser> = {
	id: string;
	readiness: string;
	user: TUser;
};

export type SuccessionCandidate<T extends SuccessionNomination> = {
	id: string;
	user: T['user'];
	currentJobTitle: string;
	readiness: T['readiness'];
	nominations: T[];
};

export type SuccessionHolder = {
	id: string;
	firstName: string;
	lastName: string;
};

export type JobTitleSuccessorsRow<
	T extends SuccessionNomination,
	THolder extends SuccessionHolder = SuccessionHolder,
> = {
	id: string;
	title: string;
	isKeyPosition: boolean;
	holders: THolder[];
	candidatesByReadiness: Record<T['readiness'], SuccessionCandidate<T>[]>;
};

export type JobTitleSuccessorsEntry<
	T extends SuccessionNomination,
	THolder extends SuccessionHolder = SuccessionHolder,
> = {
	jobTitle: {
		id: string;
		title: Localized;
		isKeyPosition: boolean;
	};
	successors: T[];
	holders: THolder[];
};

const byTitle = (a: { title: string }, b: { title: string }) => a.title.localeCompare(b.title);

const byHolderName = (a: SuccessionHolder, b: SuccessionHolder) =>
	a.lastName.localeCompare(b.lastName) ||
	a.firstName.localeCompare(b.firstName) ||
	a.id.localeCompare(b.id);

// One row per job title. Nominations are newest first; the newest readiness
// for a person decides the column, and every nomination stays on that person
// so "keer genoemd" can still list them.
export function buildJobTitleSuccessors<
	T extends SuccessionNomination,
	THolder extends SuccessionHolder = SuccessionHolder,
>(
	entries: JobTitleSuccessorsEntry<T, THolder>[],
	localize: Localize,
	readinessOptions: T['readiness'][],
): {
	keyPositions: JobTitleSuccessorsRow<T, THolder>[];
	otherPositions: JobTitleSuccessorsRow<T, THolder>[];
} {
	const rows = entries.map((entry) => {
		const candidatesByReadiness = {} as unknown as Record<T['readiness'], SuccessionCandidate<T>[]>;

		for (const readiness of readinessOptions) {
			candidatesByReadiness[readiness] = [];
		}

		for (const candidate of groupCandidates(entry.successors, localize)) {
			candidatesByReadiness[candidate.readiness]?.push(candidate);
		}

		return {
			id: entry.jobTitle.id,
			title: localize(entry.jobTitle.title)?.text ?? '',
			isKeyPosition: entry.jobTitle.isKeyPosition,
			holders: [...entry.holders].sort(byHolderName),
			candidatesByReadiness,
		};
	});

	return {
		keyPositions: rows.filter((row) => row.isKeyPosition).sort(byTitle),
		otherPositions: rows.filter((row) => !row.isKeyPosition).sort(byTitle),
	};
}

function groupCandidates<T extends SuccessionNomination>(
	nominations: T[],
	localize: Localize,
): SuccessionCandidate<T>[] {
	const rows = new Map<string, SuccessionCandidate<T>>();

	for (const nomination of nominations) {
		const existing = rows.get(nomination.user.id);

		if (existing) {
			existing.nominations.push(nomination);
		} else {
			rows.set(nomination.user.id, {
				id: nomination.user.id,
				user: nomination.user,
				currentJobTitle:
					localize(nomination.user.currentJobPosition?.jobTitle?.title ?? [])?.text ?? '',
				readiness: nomination.readiness,
				nominations: [nomination],
			});
		}
	}

	return [...rows.values()];
}
